import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { obligationApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PriorityBadge } from "@/components/shared/MoneyDisplay";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { CalendarClock, CheckCircle2, AlertTriangle, Clock, ArrowRight } from "lucide-react";

export default function ObligationsPage() {
  const { formatMoney } = useAuth();
  const [obligations, setObligations] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    obligationApi.list().then(r => { setObligations(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const generateSchedule = async () => {
    setScheduleLoading(true);
    try {
      const res = await obligationApi.schedulePayments();
      setSchedule(res.data);
    } catch (e) { console.error(e); }
    setScheduleLoading(false);
  };

  const getDaysUntil = (dateStr) => {
    const due = new Date(dateStr);
    const now = new Date();
    return Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  };

  // Dates with obligations for calendar highlighting
  const oblDates = obligations.map(o => new Date(o.due_date));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const totalDue = obligations.reduce((sum, o) => sum + o.amount, 0);
  const criticalCount = obligations.filter(o => o.priority === "CRITICAL").length;

  return (
    <div className="space-y-6" data-testid="obligations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-slate-50">Obligations Planner</h1>
          <p className="text-sm text-slate-500 mt-0.5">Coverage ratios, payment scheduling, and risk projections</p>
        </div>
        <Button onClick={generateSchedule} disabled={scheduleLoading} className="bg-cyan-600 hover:bg-cyan-500 text-slate-50 gap-2" data-testid="generate-schedule-btn">
          <CalendarClock className="w-4 h-4" /> {scheduleLoading ? "Generating..." : "Generate Schedule"}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardContent className="p-4">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Total Due</div>
            <div className="text-xl font-mono tabular text-slate-50 mt-1">{formatMoney(totalDue)}</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardContent className="p-4">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Obligations</div>
            <div className="text-xl font-mono tabular text-slate-50 mt-1">{obligations.length}</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardContent className="p-4">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Critical</div>
            <div className="text-xl font-mono tabular text-red-400 mt-1">{criticalCount}</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardContent className="p-4">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Next Due</div>
            <div className="text-xl font-mono tabular text-amber-400 mt-1">
              {obligations[0] ? `${getDaysUntil(obligations[0].due_date)}d` : "N/A"}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Obligations Table */}
        <div className="lg:col-span-2">
          <Card className="bg-slate-900/40 border-slate-800/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400 font-manrope">Upcoming Obligations</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800/50 hover:bg-transparent">
                    <TableHead className="text-[10px] text-slate-500">Priority</TableHead>
                    <TableHead className="text-[10px] text-slate-500">Claimant</TableHead>
                    <TableHead className="text-[10px] text-slate-500">Type</TableHead>
                    <TableHead className="text-[10px] text-slate-500">Due</TableHead>
                    <TableHead className="text-[10px] text-slate-500 text-right">Amount</TableHead>
                    <TableHead className="text-[10px] text-slate-500 text-right">Coverage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {obligations.map((obl, idx) => {
                    const days = getDaysUntil(obl.due_date);
                    return (
                      <TableRow
                        key={`${obl.id || "obl"}-${obl.due_date || ""}-${obl.amount || idx}`}
                        className="border-slate-800/30"
                        data-testid={`obligation-row-${obl.id}`}
                      >
                        <TableCell><PriorityBadge priority={obl.priority} /></TableCell>
                        <TableCell className="text-xs text-slate-300">{obl.claimant}</TableCell>
                        <TableCell className="text-[10px] text-slate-500 font-mono">{obl.obligation_type}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {days <= 7 ? <AlertTriangle className="w-3 h-3 text-amber-400" /> : <Clock className="w-3 h-3 text-slate-500" />}
                            <span className={`text-xs font-mono ${days <= 7 ? 'text-amber-400' : 'text-slate-400'}`}>{obl.due_date}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-sm font-mono tabular text-slate-200">{formatMoney(obl.amount)}</TableCell>
                        <TableCell className="text-right">
                          <span className={`text-xs font-mono ${obl.coverage_pct >= 100 ? 'text-emerald-400' : obl.coverage_pct >= 80 ? 'text-amber-400' : 'text-red-400'}`}>
                            {obl.coverage_pct}%
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Calendar */}
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-400 font-manrope">Obligation Calendar</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center" data-testid="obligation-calendar">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              modifiers={{ obligation: oblDates }}
              modifiersStyles={{ obligation: { backgroundColor: 'rgba(6, 182, 212, 0.15)', color: '#06B6D4', fontWeight: 600, borderRadius: '50%' } }}
              className="rounded-lg"
            />
          </CardContent>
        </Card>
      </div>

      {/* Generated Schedule */}
      {schedule && (
        <Card className="bg-slate-900/40 border-slate-800/50 neon-cyan" data-testid="payment-schedule-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-cyan-400 font-manrope">Proposed Payment Schedule</CardTitle>
              <span className="text-xs text-slate-500 font-mono">Total: {formatMoney(schedule.total_obligations)}</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {schedule.schedule?.map((item) => (
                <div key={item.obligation_id} className={`flex items-center justify-between p-3 rounded-lg border ${item.can_pay_now ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-slate-700/30 bg-slate-800/20'}`}>
                  <div className="flex items-center gap-3">
                    {item.can_pay_now ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Clock className="w-4 h-4 text-slate-500" />}
                    <div>
                      <span className="text-xs text-slate-300">{item.claimant}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <PriorityBadge priority={item.priority} />
                        <span className="text-[10px] text-slate-500">Due {item.due_date}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono tabular text-slate-200">{formatMoney(item.amount)}</div>
                    <div className={`text-[10px] font-mono ${item.coverage_pct >= 100 ? 'text-emerald-400' : 'text-amber-400'}`}>
                      Coverage: {item.coverage_pct}%
                    </div>
                    <span className={`text-[10px] ${item.recommended_action === 'Pay now' ? 'text-emerald-400' : 'text-slate-500'}`}>
                      {item.recommended_action}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
